import { useQuery } from "@tanstack/react-query"
import { locationService } from "../../services/locationService"
import { useContext, useEffect, useState, useCallback } from "react"
import UiContext from "../../context/uiProvider"
import { DESKTOP, TABLET } from "../../utils/screenSize"
import Table from "./common/Table"
import { Location } from "../../Interfaces"

export default function OrganizationLocationsTable({ organizationId }: { organizationId: number }) {
  const { deviceView } = useContext(UiContext)
  const [searchTerm, setSearchTerm] = useState<string>("")

  const { data, isLoading } = useQuery<Partial<Location[]>>(
    ["locations", "organizationId" + organizationId],
    () => locationService.getAllByOrganization(organizationId),
    {
      cacheTime: 5000,
      enabled: !!organizationId
    }
  )

  const [filteredData, setFilteredData] = useState<Location[]>([])

  useEffect(() => {
    if (data) {
      // Filter out undefined values and then apply search filter
      const validLocations = data.filter((loc): loc is Location => loc !== undefined)

      if (searchTerm) {
        const filtered = validLocations.filter(location =>
          location.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          location.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          location.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          location.state?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          location.zipcode?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
        setFilteredData(filtered)
      } else {
        setFilteredData(validLocations)
      }
    }
  }, [data, searchTerm])

  const headers: { label: string, key: string, to?: string }[] = [
    { label: "Id", key: "id", to: `/app/locations` },
    { label: "Name", key: "name", to: `/app/locations` },
  ]

  // Add two more columns on tablets and desktops
  if (deviceView >= TABLET) {
    headers.push({ label: "Address", key: "address" }, { label: "City", key: "city" }, { label: "State", key: "state" }, { label: "Zipcode", key: "zipcode" })
  }
  // Add two additional columns on desktops
  if (deviceView >= DESKTOP) {
    headers.push({ label: "Latitude", key: "latitude" }, { label: "Longitude", key: "longitude" })
  }

  const handleSearch = useCallback((query: string) => {
    setSearchTerm(query)
  }, [])

  if (isLoading) return null

  return (
    <Table
      data={filteredData}
      title={"Area Locations"}
      to="/app/addLocation"
      handleSearch={handleSearch}
      headers={headers}
    />
  )
}
